<template>
  <!-- Tags -->
  <TagManageDialog
    v-model:visibility="dialogManageVisible"
    :tags="availableTags"
    @tag-changed="loadTags()" />

  <TagCreateDialog
    v-model:visibility="dialogCreateVisible"
    @saved="(newTag) => handleNewTagCreated(newTag)"
    @closed="newTagApp = null" />

  <!-- Disconnect -->
  <ApplicationDisconnectDialog
    v-if="applicationToDelete"
    v-model:visibility="deleteApplicationDialogShown"
    :application="applicationToDelete" />

  <EmptyState
    v-if="isEmpty && !isLoading"
    :icon="LayoutGrid"
    :title="i18n.t('applicationEmptyStateTitle')"
    :text="i18n.t('applicationEmptyStateText')"
    :create-label="i18n.t('views.applications.addSoftwareButtonText')"
    :show-docs-btn="false"
    class="empty-state"
    @create="emit('open-add-application-drawer')" />

  <SmTable
    v-else
    ref="applicationTableRef"
    :data="mergedData"
    :columns="columns"
    :row-link="getApplicationLink"
    :default-sorting="{ by: 'display_name', asc: false }"
    key-field="key"
    :loading="isLoading"
    :contain-strict="true"
    @cell-click="handleCellClick">
    <!-- Status Indicator -->
    <template #statusIndicator="{ row }">
      <AccountStatusSmall :status="getStatus(row)" :show-text="false" />
    </template>

    <!-- Icon and Name -->
    <template #display_name="{ row }">
      <div class="flex items-center gap-3">
        <ApplicationIcon
          :application-name="getApplicationName(row)"
          class="shrink-0"
          :size="25"
          :application-type="getApplicationType(row)" />
        <div>
          <div flex items-center gap-3>
            <TextMultiline>{{ row.display_name }}</TextMultiline>
            <SmTooltip v-if="row.type !== 'sso' && row.read_only">
              <template #content> {{ i18n.t('readonlyDesc') }} </template>
              <span class="no-data">({{ i18n.t('readonlyShort') }})</span>
            </SmTooltip>
          </div>
          <TextMultiline v-if="row.type === 'sso'" class="no-data">
            {{
              i18n.t('viaX', {
                x: getDataFromSoftwareId(row.application_id)?.display_name,
              })
            }}
          </TextMultiline>
        </div>
      </div>
    </template>

    <!-- Tags -->
    <template #header-tags>
      <div class="flex items-center gap-2">
        {{ i18n.t('tags') }}
        <Settings
          :size="14"
          class="cursor-pointer"
          @click="dialogManageVisible = true" />
      </div>
    </template>

    <!-- Tags -->
    <template #tags="{ row }">
      <div class="flex w-full cursor-default flex-wrap items-center gap-2 py-2">
        <TagComponent
          v-for="tag in getTagsByIds(row.tags ?? [])"
          :key="tag._id"
          :tag="tag"
          :selected="selectedTags.includes(tag)"
          :style="{ maxWidth: 'calc(100% - 2rem)' }"
          class="cursor-pointer"
          @click.capture="handleTagClick(tag)" />
        <TagSelect
          show-create-tag
          :available-tags="availableTags"
          :selected="row.tags ?? []"
          @create-tag="openCreateTagDialog(row)"
          @delete-tag="removeTagFromApplication($event, row.tags, row)"
          @add-tag="addTagToApplication($event, row.tags ?? [], row)">
          <Plus :size="16" class="tag-add-icon opacity-0 transition" />
        </TagSelect>
      </div>
    </template>

    <!-- User Count -->
    <template #user_count="{ row }">
      <span>
        {{ row.user_count ?? '0' }}
      </span>
    </template>

    <!-- Costs -->
    <template #cost="{ row }">
      <span>
        {{
          row.application_costs ? getCorrectedPrice(row.application_costs) : '-'
        }}
      </span>
    </template>

    <!-- Actions -->
    <template #action="{ row }">
      <div class="table-options">
        <SmDropdown trigger="click">
          <SmDropdownItem :icon="LayoutGrid" @click="openApplicationPage(row)">
            {{ i18n.t('showApplication') }}
          </SmDropdownItem>
          <SmDropdownItem
            :icon="PencilLine"
            @click="openApplicationPage(row, true)">
            {{ i18n.t('editApplication') }}
          </SmDropdownItem>
          <SmDropdownItem
            v-if="
              isApplication(row) &&
              row?.information &&
              !row.information.sso_provider
            "
            v-require-permission="CompanyAccessRole.ADMIN"
            hover-color="var(--sm-magenta)"
            :icon="Unlink"
            @click="handleDeleteSoftwareRowClick(row)">
            {{
              i18n.t('disconnectApplication', {
                softwareName: row.display_name,
              })
            }}
          </SmDropdownItem>

          <!-- Hide Subaccoutn/SSO Application -->
          <SmDropdownItem
            v-else-if="isSSOSubAccount(row)"
            v-require-permission="CompanyAccessRole.ADMIN"
            hover-color="var(--sm-magenta)"
            :icon="EyeOff"
            @click="handleHideApplicationRowClick(row)">
            {{ i18n.t('hideApplication', { name: getApplicationName(row) }) }}
          </SmDropdownItem>
        </SmDropdown>
      </div>
    </template>
  </SmTable>
</template>

<script setup lang="ts">
  import SmTable from '@/components/sm/SmTable.vue'
  import {
    EyeOff,
    LayoutGrid,
    PencilLine,
    Plus,
    Settings,
    Unlink,
  } from 'lucide-vue-next'
  import {
    CompanyAccessRole,
    PaginationSortOrder,
    SoftwareOut,
    SoftwareService,
    SoftwareSubaccountsService,
    SoftwareTypes,
    SoftwareUpdateIn,
    SubAccountUpdateIn,
    SubAccount_Output,
    Tag,
    TagsService,
  } from '@/client'
  import { useI18n } from 'vue-i18n'
  import { useRoute, useRouter } from 'vue-router'
  import { CustomStatus } from '@/components/accounts/AccountStatusSmall.vue'
  import { Column } from '@/components/sm/SmTable.types'
  import {
    ApplicationStore,
    EnrichedSoftwareOut,
  } from '@/stores/application/application.store'
  import {
    ApplicationFilter,
    ApplicationSourceFilter,
  } from '@/stores/application/application.filter'
  import { getCorrectedPrice } from '@/utils/formatter/price'
  import { usePreferenceStore } from '@/stores/preferenceStore'
  import { ComponentExposed } from 'vue-component-type-helpers'

  const i18n = useI18n()
  const router = useRouter()

  const applicationTableRef =
    ref<ComponentExposed<typeof SmTable<EnrichedSoftwareOut | SSOSubAccount>>>()

  const dialogManageVisible = ref(false)
  const dialogCreateVisible = ref(false)

  const route = useRoute()

  const emit = defineEmits(['open-add-application-drawer'])

  // ----------------------------------------------------------------------
  // Filter
  // ----------------------------------------------------------------------

  const filter = defineModel<ApplicationFilter>('filter', {
    required: true,
    default: () => {
      return {}
    },
  })

  // ----------------------------------------------------------------------
  // Load Applications
  // ----------------------------------------------------------------------

  const {
    data: applications,
    loading: applicationsLoading,
  } = ApplicationStore.useApplications<EnrichedSoftwareOut>(
    filter,
    undefined,
    undefined,
    true
  )

  const { data: ssoApplications, loading: ssoLoading } =
    ApplicationStore.useApplications<EnrichedSoftwareOut>(
      {
        isSSOProvider: true,
      },
      undefined,
      undefined,
      true
    )

  const isLoading = computed(() => {
    return applicationsLoading.value || ssoLoading.value
  })

  const isEmpty = computed(() => {
    return totalApplicationCount.value === 0 && totalSubaccountCount.value === 0
  })

  const { count: filteredApplicationsCount } =
    ApplicationStore.useApplicationsCount(filter)
  const { count: totalApplicationCount } =
    ApplicationStore.useApplicationsCount({ isSSOProvider: false })

  const totalSubaccountCount = ref(0)
  const filteredSubaccountCount = ref(0)

  // Merge Applications with SSO Subaccount
  // If working on this consider moving the SSO_Subaccounts into a individual table to avoid merging different datasets

  export type SSOSubAccount = SubAccount_Output & {
    _id: string
    key: string
    type: 'sso'
    application_id: string
    user_count: number
    application_costs: number
    searchText: string
  }

  const ssoApplicationsById = computed(() => {
    const map = new Map<string, EnrichedSoftwareOut>()
    for (const application of ssoApplications.value) {
      map.set(application._id, application)
    }
    return map
  })

  const ssoSubaccountsAll = shallowRef<SSOSubAccount[]>([])
  const filteredSsoSubaccounts = shallowRef<SSOSubAccount[]>([])
  const mergedData = shallowRef<(EnrichedSoftwareOut | SSOSubAccount)[]>([])

  function buildSsoSubaccounts(apps: EnrichedSoftwareOut[]): SSOSubAccount[] {
    if (!apps || apps.length === 0) return []

    const subaccounts: SSOSubAccount[] = []

    for (const application of apps) {
      for (const [subAccountId, subAccount] of Object.entries(
        application.sub_accounts
      )) {
        if (!application.config.sub_accounts_filter.includes(subAccountId)) {
          const uniqueKey = `${application._id}-${subAccountId}`
          subaccounts.push({
            _id: subAccountId,
            key: uniqueKey,
            type: 'sso',
            application_id: application._id,
            user_count: application.subaccount_user_count?.[subAccountId] || 0,
            application_costs:
              application.sub_account_costs?.[subAccountId] || 0,
            ...subAccount,
            searchText: (subAccount.display_name || '').toLowerCase(),
          })
        }
      }
    }

    return subaccounts
  }

  watch(
    ssoApplications,
    (apps) => {
      const list = buildSsoSubaccounts(apps as EnrichedSoftwareOut[])
      ssoSubaccountsAll.value = list
      totalSubaccountCount.value = list.length
    },
    { immediate: true }
  )

  function applySubaccountFilters() {
    const all = ssoSubaccountsAll.value
    if (!all.length) {
      filteredSsoSubaccounts.value = []
      filteredSubaccountCount.value = 0
      return
    }

    const searchTerm = (filter.value.search || '').toLowerCase()
    const hasSearch = searchTerm.length > 0
    const tagSet = filter.value.tags?.length
      ? new Set(filter.value.tags)
      : undefined
    const hideWithoutCosts = Boolean(filter.value.hideWithoutCosts)
    const hideWithCosts = Boolean(filter.value.hideWithCosts)
    const hideWithoutAccount = Boolean(filter.value.hideWithoutAccount)

    const filtered = all.filter((subaccount) => {
      if (hasSearch && !subaccount.searchText.includes(searchTerm)) return false

      if (tagSet && !subaccount.tags?.some((tag) => tagSet.has(tag))) {
        return false
      }

      if (hideWithoutCosts && subaccount.application_costs <= 0) return false
      if (hideWithCosts && subaccount.application_costs > 0) return false
      if (hideWithoutAccount && subaccount.user_count <= 0) return false

      return true
    })

    filteredSsoSubaccounts.value = filtered
    filteredSubaccountCount.value = filtered.length
  }

  watch(
    [
      ssoSubaccountsAll,
      () => filter.value.search,
      () => filter.value.tags?.join('|'),
      () => filter.value.hideWithoutCosts,
      () => filter.value.hideWithCosts,
      () => filter.value.hideWithoutAccount,
    ],
    applySubaccountFilters,
    { immediate: true }
  )

  function rebuildMergedData() {
    const includeSso =
      !filter.value.isSource?.length ||
      filter.value.isSource.includes(ApplicationSourceFilter.SSO)

    const baseApplications = applications.value as EnrichedSoftwareOut[]

    mergedData.value = includeSso
      ? [...baseApplications, ...filteredSsoSubaccounts.value]
      : [...baseApplications]
  }

  watch(
    [applications, filteredSsoSubaccounts, () => filter.value.isSource?.join('|')],
    rebuildMergedData,
    { immediate: true }
  )

  const preferenceStore = usePreferenceStore()
  watch(
    () => preferenceStore.preferences?.currency_code,
    async () => {
      await ApplicationStore.updateApplicationCostsCacheFromApi()
    }
  )

  // ----------------------------------------------------------------------
  // scroll to last position
  // ----------------------------------------------------------------------

  watch(mergedData, async () => {
    await nextTick()
    requestAnimationFrame(() => {
      if (route.query.scroll) {
        applicationTableRef.value?.scrollTo(route.query.scroll as string)
      }
    })
  })

  // ----------------------------------------------------------------------
  // Table
  // ----------------------------------------------------------------------

  const columns = computed<Column<EnrichedSoftwareOut | SSOSubAccount>[]>(
    () => [
      {
        key: 'statusIndicator',
        label: '',
        width: '10px',
        sortable: false,
      },
      {
        key: 'display_name',
        label: i18n.t('name'),
        sortable: true,
        width: 1,
        sortFn: (a, b) => {
          if (a.display_name && b.display_name) {
            return a.display_name.localeCompare(b.display_name)
          }
          return 0
        },
        footer: formattedTotalName,
        loader: true,
      },
      {
        key: 'tags',
        label: i18n.t('tags'),
        sortable: false,
        width: 1,
        loader: true,
      },
      {
        key: 'user_count',
        label: i18n.t('account', { count: 2 }),
        sortable: true,
        width: 0.7,
        loader: true,
      },
      {
        key: 'cost',
        label: i18n.t('views.applications.table.costsLabel'),
        sortable: true,
        width: 0.7,
        sortFn: (a, b) => {
          return a.application_costs - b.application_costs
        },
        loader: true,
      },
      {
        key: 'action',
        label: '',
        width: '50px',
        sortable: false,
      },
    ]
  )

  function handleCellClick(
    _: number,
    columnKey: string,
    row: SoftwareOut | SSOSubAccount
  ) {
    if (columnKey === 'tags' || columnKey === 'action') return

    openApplicationPage(row)
  }

  const formattedTotalName = computed(() => {
    let _filteredSubAccountCount: number = filteredSubaccountCount.value
    if (!filter.value.isSource?.includes(ApplicationSourceFilter.SSO)) {
      _filteredSubAccountCount = 0
    }

    const filtered: number =
      filteredApplicationsCount.value + _filteredSubAccountCount
    const total: number =
      totalApplicationCount.value + totalSubaccountCount.value

    if (filtered === 0) {
      return `0 ${i18n.t('application', { count: 2 })}`
    }

    const apps = i18n.t('applications', { count: filtered })

    if (filtered === total) {
      return `${filtered} ${apps}`
    }

    const xOfY = i18n.t('XofY', { x: filtered, y: total })
    return `${xOfY} ${apps}`
  })

  // ----------------------------------------------------------------------
  // Actions
  // ----------------------------------------------------------------------

  const deleteApplicationDialogShown = ref(false)
  const applicationToDelete = ref<SoftwareOut>()
  function handleDeleteSoftwareRowClick(app: SoftwareOut) {
    applicationToDelete.value = app
    deleteApplicationDialogShown.value = true
  }

  async function handleHideApplicationRowClick(subaccount: SSOSubAccount) {
    const _current_app = await ApplicationStore.getApplicationById(
      subaccount.application_id
    )

    const _current_subaccounts = _current_app?.config.sub_accounts_filter || []

    SoftwareService.putSoftwareApiV1SoftwareSoftwareSoftwareIdPut({
      softwareId: subaccount.application_id,
      requestBody: {
        config: {
          sub_accounts_filter: [..._current_subaccounts, subaccount._id],
        },
      },
    }).then(() => {
      ApplicationStore.updateApplicationCacheFromApi()
    })
  }

  function updateSubAccount(
    appId: string,
    subAccountId: string,
    newApp: SubAccountUpdateIn
  ) {
    SoftwareSubaccountsService.updateSubAccountApiV1SoftwareSoftwareSoftwareIdSubAccountsSubAccountIdPatch(
      {
        softwareId: appId,
        subAccountId: subAccountId,
        requestBody: newApp,
      }
    ).then(async () => {
      ApplicationStore.updateApplicationCacheFromApi()
    })
  }

  // ----------------------------------------------------------------------
  // Tags
  // ----------------------------------------------------------------------
  const availableTags = ref<Tag[]>([])
  const selectedTags = ref<Tag[]>([])

  function getTagsByIds(ids: string[] = []): Tag[] {
    return availableTags.value?.filter((tag) => ids?.includes(tag._id))
  }

  function removeTagFromApplication(
    tagId: string,
    tagsBefore: string[],
    application: SoftwareOut | SSOSubAccount
  ) {
    const updateTags = tagsBefore.filter((existing) => existing != tagId)

    if (application.type === 'sso') {
      // SSO
      const applicationUpdate: SubAccountUpdateIn = {
        tags: updateTags,
      }
      updateSubAccount(
        application.application_id,
        application._id,
        applicationUpdate
      )
    } else {
      //App
      const applicationUpdate: SoftwareUpdateIn = { tags: updateTags }
      ApplicationStore.updateApplication(application._id, applicationUpdate)
    }
  }

  function addTagToApplication(
    tagId: string,
    tagsBefore: string[],
    application: SoftwareOut | SSOSubAccount
  ) {
    const updateTags = new Array(...tagsBefore)
    updateTags.push(tagId)
    if (application.type === 'sso') {
      // SSO
      const applicationUpdate: SubAccountUpdateIn = {
        tags: updateTags,
      }
      updateSubAccount(
        application.application_id,
        application._id,
        applicationUpdate
      )
    } else {
      //App
      const applicationUpdate: SoftwareUpdateIn = { tags: updateTags }
      ApplicationStore.updateApplication(application._id, applicationUpdate)
    }
  }

  function handleTagClick(tag: Tag) {
    if (selectedTags.value.includes(tag)) {
      selectedTags.value.splice(selectedTags.value.indexOf(tag), 1)
    } else {
      selectedTags.value.push(tag)
    }

    filter.value.tags = selectedTags.value.map((tag) => tag._id)
  }

  const newTagApp = ref<SoftwareOut | SSOSubAccount | null>(null)

  function openCreateTagDialog(app: SoftwareOut | SSOSubAccount) {
    dialogCreateVisible.value = true
    newTagApp.value = app
  }

  function handleNewTagCreated(tag: Tag) {
    loadTags()
    addTagToApplication(tag._id, newTagApp.value?.tags || [], newTagApp.value!)
    newTagApp.value = null
  }

  function loadTags() {
    TagsService.getTagsApiV1ManagementTagsGet({
      sortBy: 'sorting',
      sortOrder: PaginationSortOrder.DESC,
    }).then((tags) => {
      availableTags.value = tags
    })
  }

  onMounted(async () => {
    loadTags()
  })

  // ----------------------------------------------------------------------
  // Utils
  // ----------------------------------------------------------------------

  function isSSOSubAccount(
    app: SoftwareOut | SSOSubAccount
  ): app is SSOSubAccount {
    return !isApplication(app)
  }

  function isApplication(app: SoftwareOut | SSOSubAccount): app is SoftwareOut {
    return 'software_name' in app
  }

  function getDataFromSoftwareId(applicationId: string) {
    return ssoApplicationsById.value.get(applicationId)
  }

  function getApplicationName(app: SoftwareOut | SSOSubAccount) {
    if (isApplication(app)) {
      return app.software_name
    }
    return app._id
  }

  function getApplicationType(
    app: SoftwareOut | SSOSubAccount
  ): SoftwareTypes | 'SSO' | null {
    if (isSSOSubAccount(app)) {
      return 'SSO'
    }
    return app.type
  }

  function openApplicationPage(
    app: SoftwareOut | SSOSubAccount,
    editMode: boolean = false
  ) {
    const params = getApplicationParams(app)
    if (!params.id) {
      return '#'
    }

    router.push({
      name: 'ApplicationDetailsOverview',
      params,
      query: {
        sso: app.type === 'sso' ? 'true' : 'false',
        edit: editMode ? 'true' : 'false',
      },
    })
  }

  function getApplicationParams(app: SoftwareOut | SSOSubAccount) {
    if (!app._id) {
      return {}
    }
    if (app.type === 'sso') {
      return {
        id: app.application_id,
        subAccountId: app._id,
      }
    } else {
      return {
        id: app._id,
        subAccountId: Object.keys(app.sub_accounts)[0],
      }
    }
  }

  function getApplicationLink(app: SoftwareOut | SSOSubAccount) {
    const params = getApplicationParams(app)
    if (!params.id) {
      return '#'
    }

    const { href } = router.resolve({
      name: 'Application',
      params,
      query: {
        sso: app.type === 'sso' ? 'true' : 'false',
      },
    })
    return href
  }

  function getStatus(app: SoftwareOut | SSOSubAccount): CustomStatus {
    if (app.type === 'sso') {
      return {
        color: 'var(--sm-blue)',
        text: i18n.t('ssoManaged', {
          name: getDataFromSoftwareId(app.application_id)?.display_name,
        }),
      }
    }

    if (app.authentication_expired) {
      return {
        color: 'var(--sm-magenta)',
        text: i18n.t('reauthenticationRequired'),
      }
    }

    if (app.awaits_authorization) {
      return {
        color: 'var(--sm-yellow)',
        text: i18n.t('authPending.title'),
      }
    }

    switch (app.type) {
      case SoftwareTypes.MANUALLY:
        return {
          color: ' var(--sm-contrast-muted)',
          text: i18n.t('general.applicationTypeTag.manually'),
        }
      case SoftwareTypes.TOKEN:
      case SoftwareTypes.BOT:
      case SoftwareTypes.OAUTH:
        return {
          color: 'var(--sm-green)',
          text: i18n.t(`general.applicationTypeTag.${app.type}`),
        }
      default:
        return {
          color: 'var(--sm-orange)',
          text: i18n.t('general.unknown'),
        }
    }
  }
</script>

<style lang="scss">
  .sm-table-row {
    &:hover {
      .tag-add-icon {
        opacity: 1;
      }
    }
  }
</style>
